/**
 * @file usePlanGovernance.js
 * @description Governance state hook for the plan workspace.
 *
 * Owns:
 *   ep 3  GET  /api/v1/plans/{id}/governance      — eligibility + amendment state
 *   ep 6  POST /api/v1/plans/{id}/submit           — plan submission
 *   ep 12 POST /api/v1/plans/{id}/amendments/begin — begin amendment
 *   ep 11 POST /api/v1/plans/{id}/withdraw         — withdraw submission
 *
 * Supplies to PlanWorkspace:
 *   canSubmit            — boolean: plan can be submitted for approval
 *   canProposeAmendment  — boolean: plan can begin an amendment
 *   canCreateTemplate    — boolean: plan can be saved as certified template
 *   inAmendmentMode      — boolean: amendment is open/pending
 *   amendment            — amendment object for AmendmentStateBanner
 *   onSubmit()           — POST /plans/{id}/submit
 *   onBeginAmendment()   — POST /plans/{id}/amendments/begin
 *   onCancelAmendment()  — delegated to CancelAmendmentDialog (not owned here)
 *   onForkPlan()         — delegated to SaveAsNewPlanDialog (not owned here)
 *   onCreateTemplate()   — delegated to CreateCertifiedPlanCandidateDialog (not owned here)
 *   submitting           — boolean: submit mutation in progress
 *
 * Amendment object shape (consumed by AmendmentStateBanner):
 *   amendmentId, amendmentStatus, successorPlanId, initiatedBy, initiatedAt,
 *   economicEffectiveDate, draftVersion, effectiveDate, reason,
 *   approvedVersion (number), approvedEffectiveFrom, approvedEffectiveTo,
 *   isActor (caller is initiator)
 *
 * Spec refs:
 *   api_spec_fileA_v4_final.md §1.3  — GET /api/v1/plans/{id}/governance
 *   api_spec_fileA_v4_final.md §1.6  — POST /api/v1/plans/{id}/submit
 *   api_spec_fileA_v4_final.md §1.12 — POST /api/v1/plans/{id}/amendments/begin
 *
 * @author data-fit LLC · DecisionFlow · S807
 * @since S807
 * @changelog
 *   S807  Initial build
 *   S815  approvedVersion flattened to number (was object — mismatched AmendmentStateBanner
 *         PropTypes.number). approvedEffectiveFrom / approvedEffectiveTo added as top-level
 *         fields on enrichedAmendment so AmendmentStateBanner can render them directly.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Normalise ep 3 response
// ---------------------------------------------------------------------------

/**
 * Spec §1.3 response:
 * {
 *   planId, planStatus, version,
 *   canSubmit, canAmend, canSubmitAmendment, canWithdraw, canFork, canSaveAsTemplate,
 *   governanceBlockers[],
 *   amendment: { amendmentId, amendmentStatus, successorPlanId, initiatedBy, initiatedAt, economicEffectiveDate },
 *   approvalHistory[],
 *   crlOverride: { hasOverride, overrideReason }
 * }
 */
function normalizeGovernance(raw, currentUserId) {
  const amendment = raw.amendment ?? null;

  const inAmendmentMode = Boolean(
    amendment &&
    (amendment.amendmentStatus === 'open' ||
     amendment.amendmentStatus === 'pending_approval')
  );

  // Enrich amendment for AmendmentStateBanner consumption.
  // S815 FIX: approvedVersion is a NUMBER (banner renders V{approvedVersion}).
  // approvedEffectiveFrom / approvedEffectiveTo are separate top-level fields
  // on enrichedAmendment — banner reads them directly for date display.
  // Previously approvedVersion was an object {version, effectiveFrom, effectiveTo}
  // which mismatched AmendmentStateBanner PropTypes.number and broke rendering.
  const enrichedAmendment = amendment
    ? {
        amendmentId:          amendment.amendmentId     ?? amendment.amendment_id     ?? null,
        amendmentStatus:      amendment.amendmentStatus ?? amendment.amendment_status ?? null,
        successorPlanId:      amendment.successorPlanId ?? amendment.successor_plan_id ?? null,
        initiatedBy:          amendment.initiatedBy     ?? amendment.initiated_by     ?? null,
        initiatedAt:          amendment.initiatedAt     ?? amendment.initiated_at     ?? null,
        economicEffectiveDate: amendment.economicEffectiveDate ?? amendment.economic_effective_date ?? null,
        // effectiveDate + reason for AmendmentStateBanner + CancelAmendmentDialog
        effectiveDate:        amendment.economicEffectiveDate ?? amendment.economic_effective_date ?? 'Not specified',
        reason:               amendment.amendmentReasonNote   ?? amendment.amendment_reason_note   ?? '',
        draftVersion:         (raw.version ?? 1) + 1,
        hasEnteredGovernance: (amendment.amendmentStatus ?? amendment.amendment_status) === 'pending_approval',
        // S815: approvedVersion as NUMBER — AmendmentStateBanner PropTypes.number.isRequired
        approvedVersion:      raw.version ?? 1,
        // S815: separate date fields — banner reads amendment.approvedEffectiveFrom directly
        approvedEffectiveFrom: raw.effectiveFrom ?? raw.effective_from ?? 'Not specified',
        approvedEffectiveTo:   raw.effectiveTo   ?? raw.effective_to   ?? null,
        isActor: currentUserId
          ? String(amendment.initiatedBy ?? amendment.initiated_by) === String(currentUserId)
          : false,
        canAct: currentUserId
          ? String(amendment.initiatedBy ?? amendment.initiated_by) === String(currentUserId)
          : false,
      }
    : null;

  return {
    planStatus:   raw.planStatus ?? raw.plan_status ?? 'draft',
    version:      raw.version    ?? 1,
    canSubmit:    raw.canSubmit  ?? false,
    canAmend:     raw.canAmend   ?? false,
    canWithdraw:  raw.canWithdraw ?? false,
    canFork:      raw.canFork    ?? false,
    canCreateTemplate: raw.canSaveAsTemplate ?? false,
    canProposeAmendment: raw.canAmend ?? false,
    governanceBlockers: raw.governanceBlockers ?? [],
    amendment:          enrichedAmendment,
    inAmendmentMode,
    approvalHistory: raw.approvalHistory ?? [],
    crlOverride:    raw.crlOverride ?? { hasOverride: false, overrideReason: null },
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {number} planId
 * @param {object} [currentUser]
 */
export default function usePlanGovernance(planId, currentUser) {
  const [governance, setGovernance] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const mountedRef = useRef(true);
  const abortRef   = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ---------------------------------------------------------------------------
  // Load ep 3
  // ---------------------------------------------------------------------------
  const load = useCallback(async () => {
    if (!planId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/v1/plans/${planId}/governance`, {
        credentials: 'include',
        signal: controller.signal,
      });

      if (!mountedRef.current || controller.signal.aborted) return;

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(
          errBody.detail || `Unable to load governance state (${response.status}).`
        );
      }

      const data = await response.json();

      if (!mountedRef.current || controller.signal.aborted) return;

      setGovernance(normalizeGovernance(data, currentUser?.user_account_id));

    } catch (err) {
      if (!mountedRef.current || err.name === 'AbortError') return;
      setError(err.message || 'Unable to load governance state.');
    } finally {
      if (mountedRef.current && !abortRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [planId, currentUser?.user_account_id]);

  useEffect(() => {
    load();
    return () => { abortRef.current?.abort(); };
  }, [load]);

  // ---------------------------------------------------------------------------
  // Submit plan (ep 6)
  // ---------------------------------------------------------------------------
  const onSubmit = useCallback(async () => {
    if (!planId || submitting) return;

    setSubmitting(true);

    try {
      const response = await fetch(`/api/v1/plans/${planId}/submit`, {
        method:      'POST',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({}),
      });

      if (!mountedRef.current) return;

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(
          errBody.detail || `Submission failed (${response.status}).`
        );
      }

      await load();

    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message || 'Submission failed.');
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [planId, submitting, load]);

  // ---------------------------------------------------------------------------
  // Begin amendment (ep 12)
  // ---------------------------------------------------------------------------
  const onBeginAmendment = useCallback(async (payload) => {
    if (!planId) return;

    try {
      const response = await fetch(`/api/v1/plans/${planId}/amendments/begin`, {
        method:      'POST',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify(payload),
      });

      if (!mountedRef.current) return;

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(
          errBody.detail || `Unable to begin amendment (${response.status}).`
        );
      }

      await load();

    } catch (err) {
      if (!mountedRef.current) return;
      throw err;
    }
  }, [planId, load]);

  const onForkPlan       = useCallback(() => {}, []);
  const onCreateTemplate = useCallback(() => {}, []);

  return {
    canSubmit:           governance?.canSubmit           ?? false,
    canProposeAmendment: governance?.canProposeAmendment ?? false,
    canCreateTemplate:   governance?.canCreateTemplate   ?? false,
    inAmendmentMode:     governance?.inAmendmentMode     ?? false,
    amendment:           governance?.amendment           ?? null,
    onSubmit,
    onBeginAmendment,
    onForkPlan,
    onCreateTemplate,
    submitting,
    loading,
    error,
    refresh: load,
  };
}
